import {
    ComboBox,
} from '@carbon/react';
import { defineMessages, useIntl } from 'react-intl';
import { useCallback, useMemo } from 'react';

import styles from './UserRoleSelect.module.scss';

import sharedMessages from 'Components/utils/sharedMessages';
import { UserRole } from 'api/dtos/user';

const roles: UserRole[] = [UserRole.Admin, UserRole.TeamMember];

type Props = {
    id: string;
    role: UserRole;
    isLoading: boolean;
    isDisabled?: boolean;
    onSelectRole: (updatedRole: UserRole, callback?: () => void) => void;
}

const UserRoleSelect = ({
    id,
    role,
    onSelectRole,
    isDisabled,
    isLoading = false,
}: Props) => {
    const intl = useIntl();

    const onChange = useCallback(({ selectedItem }: {selectedItem?: UserRole | null}) => {
        if (selectedItem) {
            onSelectRole(selectedItem);
        }
    }, [onSelectRole]);

    const itemToString = useCallback((item: UserRole | null) => {
        if (isLoading) {
            return intl.formatMessage(sharedMessages.loading);
        }

        if (item === null) {
            return '';
        }

        if (item === UserRole.Owner) {
            return intl.formatMessage(messages.owner);
        }

        if (item === UserRole.Guest) {
            return intl.formatMessage(messages.guest);
        }

        if (item === UserRole.Moderator) {
            return intl.formatMessage(messages.moderator);
        }

        if (item === UserRole.Admin) {
            return intl.formatMessage(messages.admin);
        }

        return intl.formatMessage(messages.teamMember);
    }, [intl, isLoading]);

    const value = useMemo(() => {
        if (role === UserRole.Admin) {
            return intl.formatMessage(messages.admin);
        }

        if (role === UserRole.Owner) {
            return intl.formatMessage(messages.owner);
        }

        if (role === UserRole.Moderator) {
            return intl.formatMessage(messages.moderator);
        }

        if (role === UserRole.Guest) {
            return intl.formatMessage(messages.guest);
        }

        return intl.formatMessage(messages.teamMember);
    }, [intl, role]);

    return (
        <div className={styles.container}>
            <ComboBox
                onChange={onChange}
                className={styles.combobox}
                id={id}
                items={roles}
                itemToString={itemToString}
                value={value}
                titleText=""
                helperText=""
                disabled={isDisabled}
            />
        </div>
    );
};

const messages = defineMessages({
    owner: {
        id: 'src.Components.Common.UserRoleSelect.UserRoleSelect.owner',
        defaultMessage: 'Właściciel',
    },
    admin: {
        id: 'src.Components.Common.UserRoleSelect.UserRoleSelect.admin',
        defaultMessage: 'Administrator',
    },
    teamMember: {
        id: 'src.Components.Common.UserRoleSelect.UserRoleSelect.teamMember',
        defaultMessage: 'Członek zespołu',
    },
    guest: {
        id: 'src.Components.Common.UserRoleSelect.UserRoleSelect.guest',
        defaultMessage: 'Gość',
    },
    moderator: {
        id: 'src.Components.Common.UserRoleSelect.UserRoleSelect.moderator',
        defaultMessage: 'Moderator',
    },
});

export default UserRoleSelect;
